// owner-map: OP-07b.spa
// Every copy-link network call lives here, so the components stay pure-props and
// App.jsx grows by a hook CALL rather than a state cluster (hooks/ convention).
//
// FOUR THINGS ABOUT THIS FEATURE SHAPE THIS FILE. Each one fails silently if
// ignored; the capsule (docs/capsules/forecast-copy-links.md) is the long form.
//
// 1. LIVENESS IS THE SERVER'S ANSWER. `listCopyLinks` derives each row's `state`
//    through the module-private `isLive` (server/handlers/copyLinks.js:68, used
//    at :1302), and that predicate fails CLOSED on an unparseable `expires_at`.
//    We cannot agree with it from here: the handler passes `expires_at` through
//    unnormalised (:1295-1300, because `toISOString()` throws RangeError on a
//    non-finite date), so a non-finite expiry reaches the browser as `null` — and
//    a client-side recomputation would call that row live for a link redeem
//    refuses with 410. So this hook stores `state` verbatim, never re-derives it,
//    and never re-sorts the list by it. `expiresAt` is display material (see
//    lib/copyLinks/state.js).
//
// 2. THE THREE STATES ARE A PRIORITY ORDER, not three independent facts:
//    `r.revoked_at ? "revoked" : isLive(r) ? "live" : "expired"` (:1302). A link
//    that is both revoked and past expiry reports "revoked". Anything derived
//    here therefore tests `state === "live"` positively rather than ruling out
//    the other two.
//
// 3. THE SECRET EXISTS EXACTLY ONCE. Mint returns the full capability URL with
//    the secret in the fragment (:442-446); the server stored only a hash
//    (:304, 333), so nothing can re-derive it afterwards. Dropping a freshly
//    minted link strands a live capability for up to 365 days. Hence: `minted` is
//    set BEFORE the follow-up list refresh, is never cleared by a later failure,
//    and only `clearMinted()` clears it. `mint()` also RETURNS the body, so a
//    caller has it even if it ignores the state.
//
// 4. REVOCATION IS NEVER RETROSPECTIVE (capsule, §5.5). It stops new redemptions;
//    existing copies stay with the recipient forever. Nothing here may imply
//    recall — not a message, not a field name.
//
// It routes through lib/apiClient.js rather than calling `fetch` directly. Two
// reasons, both concrete: apiClient centralises the 401 → /auth/login redirect
// (returning a never-resolving promise, so no caller sees a partial result) and
// turns a network failure into `{ok:false,status:0}` instead of a throw; and the
// `strayFetch` ratchet counter (app/scripts/ratchet.mjs, direction lte, goal 0)
// treats a bare `fetch(` outside that layer as a regression, so the plan's inline
// helper would have failed a hard gate.
import { beginIntent } from "../lib/telemetry/instrument";
import { intentArgs } from "../lib/telemetry/uiIntents.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiGet, apiPost } from "../lib/apiClient.js";
import { isValidExpiryDays, MAX_EXPIRY_DAYS } from "../lib/copyLinks/state.js";

/**
 * The feature is switched off in this deployment. `GET /api/copy-links` has
 * exactly ONE 404 path — `gateClosed` (server/handlers/copyLinks.js:1240 → :122),
 * which answers 404 rather than 403 so the flag is not probeable — so on that
 * endpoint a 404 means this and nothing else.
 */
export const UNAVAILABLE_MESSAGE = "Copy links aren't turned on for your account.";

/**
 * A 503 is a DIFFERENT condition from the gate's 404, with the same user-visible
 * effect: something the feature depends on is not there. It stays distinct from
 * UNAVAILABLE_MESSAGE because conflating them sends an operator to the wrong file.
 *
 * IT NAMES NO CAUSE, AND THAT IS THE POINT. Every one of the four handlers can
 * answer 503 for TWO different reasons — `!pool` ("Persistence disabled") and
 * `!cfg` ("copy links are not configured") — so from a status code alone the
 * client cannot tell which, and a message that picked one would be a confident
 * false diagnosis half the time. This string previously read "Copy links are not
 * configured in this deployment", which told an operator with a DOWN DATABASE to
 * go and check their `COPY_LINKS_*` env vars. A vague true statement beats a
 * specific wrong one.
 *
 * The real cause is not lost, only relocated: `describeFailure` carries the
 * server's own wording through as `serverError`, and the panel puts it on a
 * `data-*` attribute where an operator finds it and a user does not.
 */
export const SERVICE_UNAVAILABLE_MESSAGE =
  "Copy links aren’t available right now. Try again in a minute.";

/** apiClient reports a fetch rejection (offline, DNS, abort) as status 0. */
export const NETWORK_MESSAGE = "Sayer couldn't be reached. Check your connection and try again.";

/** Mirrors mint's own range refusal (server/handlers/copyLinks.js:169-171). */
export const EXPIRY_MESSAGE = `Expiry must be between 1 and ${MAX_EXPIRY_DAYS} days.`;

/**
 * The exact body `gateClosed` sends (server/handlers/copyLinks.js:122). Used ONLY
 * to give a gated mint/revoke coherent copy: on those two endpoints a 404 is
 * ambiguous (mint's "forecast not found" :216, revoke's "copy link not found"
 * :1145), and there is no status code that separates them. Best-effort by
 * construction — if that string ever changes we fall back to passing the
 * server's message through, so a stale match degrades the wording and nothing
 * else. The authoritative signal stays the list read, whose 404 needs no
 * matching at all.
 */
const GATE_404_ERROR = "not found";

/**
 * Map a status to a KIND. `{ok:false,status:0}` is apiClient's network/abort
 * sentinel, not an HTTP code.
 *
 * THE RULE EVERY ARM HERE OBEYS: a kind must not be narrower than the set of
 * conditions that produce its status. 503 broke it — it was labelled
 * "unconfigured" while `!pool` and `!cfg` both produce it — and the label is what
 * the UI then turned into a false diagnosis. Audited the rest at the same time,
 * against the four handlers' actual bodies, so it need not be redone:
 *
 *   400 "invalid"    — six distinct bodies, all genuinely malformed input. Generic
 *                      enough, and the server's own message is passed through.
 *   403 "forbidden"  — one body ("invalid copy link").
 *   404 "not_found"  — three bodies; the gate's is disambiguated separately below,
 *                      by exact match, and the other two are distinct strings.
 *   409 "conflict"   — one body ("cannot copy a deleted or resolved forecast").
 *   500 "server"     — the catch-all, generic by construction.
 *
 * The nearest miss is status 0 → "network": apiClient documents that sentinel as
 * "offline / DNS / CORS / **abort**", and an abort is not a network failure. It is
 * not reachable here — every abort on this surface is a superseded or unmounted
 * list read, and `refresh` returns before classifying those — so the label is
 * accurate for every path that reaches it. Worth knowing if a caller ever starts
 * passing a signal to `mint` or `revoke`.
 */
function classifyStatus(status) {
  if (status === 0) return "network";
  // NOT "unconfigured". Each handler 503s for either `!pool` or `!cfg`, and the
  // status alone cannot separate them — a label narrower than the conditions that
  // produce it is an inference the next reader will trust.
  if (status === 503) return "dependencyUnavailable";
  if (status === 400) return "invalid";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 409) return "conflict";
  return "server";
}

/** The server's own `{error}` string, when it sent a usable one. */
function serverMessage(body) {
  const raw = body && typeof body.error === "string" ? body.error.trim() : "";
  return raw || null;
}

/**
 * Turn one failed apiClient reply into `{message, kind, status}`.
 *
 * @param {{status: number, body: unknown}} r  the apiClient reply
 * @param {boolean} gateIsUnambiguous  true for `GET /api/copy-links`, whose only
 *   404 is the gate; false for mint/revoke, where a 404 may be a real missing
 *   resource
 */
function describeFailure(r, gateIsUnambiguous) {
  let kind = classifyStatus(r.status);
  // Kept verbatim even where the user-facing `message` replaces it, so the true
  // cause survives for the operator-facing channel. Never rendered as text.
  const serverError = serverMessage(r.body);
  if (kind === "not_found" && (gateIsUnambiguous || serverMessage(r.body) === GATE_404_ERROR)) {
    kind = "unavailable";
  }
  if (kind === "unavailable") {
    return { message: UNAVAILABLE_MESSAGE, kind, status: r.status, serverError };
  }
  if (kind === "dependencyUnavailable") {
    return { message: SERVICE_UNAVAILABLE_MESSAGE, kind, status: r.status, serverError };
  }
  if (kind === "network") return { message: NETWORK_MESSAGE, kind, status: r.status, serverError };
  // Every remaining refusal on this surface carries deliberate user-facing copy
  // ("cannot copy a deleted or resolved forecast", "this forecast is not
  // registered in the data plane yet…"), so pass it through rather than
  // paraphrasing it.
  return {
    message: serverError || "Couldn't update copy links for this forecast. Try again.",
    kind,
    status: r.status,
    serverError,
  };
}

/**
 * Owner-side copy-link state for one forecast: the list, mint, and revoke.
 *
 * @param {object} args
 * @param {string|null|undefined} args.forecastId  the SERVER UUID (rowToWire's
 *   `forecastId`), never the client_id — both are UUIDs, so a client_id passes
 *   the server's format guard and matches nothing (copyLinks.js:1250-1253).
 *   Falsy is a supported state (the panel may render before it resolves): no
 *   request is made and `loading` settles false.
 * @param {Function} [args.apiGetImpl]   injectable for tests (lib/apiClient apiGet)
 * @param {Function} [args.apiPostImpl]  injectable for tests (lib/apiClient apiPost)
 * @returns {{
 *   links: Array<object>, liveCount: number,
 *   loading: boolean, minting: boolean, revokingId: string|null,
 *   error: string|null, errorKind: string|null, errorStatus: number|null,
 *   availability: "unknown"|"available"|"unavailable"|"dependencyUnavailable",
 *   availabilityDetail: string|null,
 *   minted: {copyId: string, url: string, expiresAt?: string}|null,
 *   mint: (opts?: {expiryDays?: unknown, includeChat?: unknown}) => Promise<object|null>,
 *   revoke: (copyId: string) => Promise<boolean>,
 *   refresh: () => Promise<boolean>,
 *   clearMinted: () => void, clearError: () => void,
 * }}
 */
export function useCopyLinks({ forecastId, apiGetImpl = apiGet, apiPostImpl = apiPost }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minting, setMinting] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [failure, setFailure] = useState(null);
  const [availability, setAvailability] = useState("unknown");
  // The server's OWN wording for whatever made this plane unusable, kept beside
  // `availability` so an operator can tell `!pool` from `!cfg`. Operator-facing
  // only — it is never rendered as text.
  const [availabilityDetail, setAvailabilityDetail] = useState(null);
  const [minted, setMinted] = useState(null);

  // Monotonic list-read sequence. The guard is what stops a SUPERSEDED read from
  // overwriting a newer one — reachable by switching forecasts, or by a mint's
  // follow-up refresh overtaking the mount read. The AbortController below is the
  // optimisation; this is the correctness.
  const seqRef = useRef(0);
  const listAbortRef = useRef(null);
  const mintingRef = useRef(false);
  const aliveRef = useRef(true);

  // Own effect with an empty dep array, so its cleanup runs on UNMOUNT ONLY — the
  // list effect's cleanup also fires on every forecastId change, which is not the
  // same question.
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const clearMinted = useCallback(() => setMinted(null), []);
  const clearError = useCallback(() => setFailure(null), []);

  const refresh = useCallback(async () => {
    if (!forecastId) {
      // The plan put this early return AHEAD of its own
      // `finally { setLoading(false) }` (plan line 337), so a panel rendered
      // before its forecast id resolved spun forever.
      setLinks([]);
      setLoading(false);
      return false;
    }
    // ARM THE CONTROLLER BEFORE THE AWAIT (frontend booby-trap §7): a controller
    // created after the await is unreachable by the cleanup that is supposed to
    // cancel it. Superseding an in-flight read also aborts it.
    const previous = listAbortRef.current;
    const ac = new AbortController();
    listAbortRef.current = ac;
    const seq = seqRef.current + 1;
    seqRef.current = seq;
    if (previous) previous.abort();
    setLoading(true);

    const r = await apiGetImpl(`/api/copy-links?forecastId=${encodeURIComponent(forecastId)}`, {
      signal: ac.signal,
    });

    // Superseded or unmounted: drop the answer entirely. The newest read owns
    // `loading`, so this cannot strand a spinner.
    if (!aliveRef.current || seq !== seqRef.current) return false;
    setLoading(false);
    if (!r.ok) {
      const f = describeFailure(r, true);
      setFailure(f);
      // Only the two "not usable here" causes move availability. A 500 or a
      // dropped connection says nothing about whether the feature exists in this
      // plane, and treating it as absence would hide a working UI.
      if (f.kind === "unavailable" || f.kind === "dependencyUnavailable") {
        setAvailability(f.kind);
        setAvailabilityDetail(f.serverError);
      }
      return false;
    }
    setAvailability("available");
    setAvailabilityDetail(null);
    setFailure(null);
    // `state` verbatim, order verbatim (the server's created_at DESC, :1267).
    setLinks(Array.isArray(r.body?.links) ? r.body.links : []);
    return true;
  }, [forecastId, apiGetImpl]);

  useEffect(() => {
    void refresh();
    return () => {
      // Cancels the read this effect run started (or a mutation's follow-up read,
      // which is equally stale once the forecast changed).
      const ac = listAbortRef.current;
      if (ac) ac.abort();
    };
  }, [refresh]);

  const mint = useCallback(
    async ({ expiryDays, includeChat } = {}) => {
      // Single-flight. Each mint is a real, separately-revocable 90-day
      // capability, so a double-click must not create one whose URL the owner
      // never sees — that link is unrevocable in practice because they don't know
      // it exists. Returns null; `minting` is what a button should disable on.
      if (mintingRef.current) return null;
      // Pre-validated rather than round-tripped: the server's own range check is
      // the authority (copyLinks.js:169-171) and isValidExpiryDays mirrors it,
      // including "absent means the 90-day default".
      if (!isValidExpiryDays(expiryDays)) {
        setFailure({ message: EXPIRY_MESSAGE, kind: "invalid", status: null });
        return null;
      }
      const body = {};
      if (expiryDays != null) body.expiryDays = expiryDays;
      // ONLY a real boolean, else omitted. A non-boolean is a 400
      // (copyLinks.js:192-193) — deliberately, because both readings of
      // `includeChat: "false"` are wrong — and `undefined` legitimately means
      // "no chat", so omission is the safe normalisation rather than a guess.
      if (typeof includeChat === "boolean") body.includeChat = includeChat;

      mintingRef.current = true;
      // The user's copy link (P2 design §4.6); never the link or its secret.
      const intent = beginIntent(
        "copy_link.create",
        { forecast_id: forecastId },
        { origin: "copy.create", surface: "copy" },
      );
      setMinting(true);
      setFailure(null);
      let r;
      try {
        r = await apiPostImpl(
          `/api/forecasts/${encodeURIComponent(forecastId)}/copy-links`,
          body,
          ...intentArgs(intent),
        );
      } catch (e) {
        intent.end("error");
        throw e;
      } finally {
        mintingRef.current = false;
        setMinting(false);
      }
      intent.end(r.ok ? "ok" : "error");
      if (!r.ok) {
        setFailure(describeFailure(r, false));
        // An earlier `minted` is NOT cleared: it is a live capability, and losing
        // its URL because a later attempt failed is the one unrecoverable
        // outcome on this path.
        return null;
      }
      // Parked BEFORE the refresh, and the value is returned too. Both matter:
      // the refresh below can fail, and nothing anywhere can reconstruct this URL.
      setMinted(r.body);
      // Not awaited-for-success: a failed list read is reported through `error`
      // but must never look like a failed mint.
      await refresh();
      return r.body;
    },
    [forecastId, apiPostImpl, refresh],
  );

  const revoke = useCallback(
    async (copyId) => {
      setRevokingId(copyId);
      setFailure(null);
      // Never the link or its secret.
      const intent = beginIntent(
        "copy_link.revoke",
        { forecast_id: forecastId },
        { origin: "copy-links.revoke", surface: "copy-links" },
      );
      let r;
      try {
        r = await apiPostImpl(
          `/api/copy-links/${encodeURIComponent(copyId)}/revoke`,
          undefined,
          ...intentArgs(intent),
        );
      } catch (e) {
        intent.end("error");
        throw e;
      } finally {
        if (aliveRef.current) setRevokingId(null);
      }
      intent.end(r.ok ? "ok" : "error");
      if (!r.ok) {
        setFailure(describeFailure(r, false));
        // No optimistic flip. A falsy/failed Bigtable write means the capability
        // may still be live (copyLinks.js:1177-1183), and showing "Revoked" for a
        // link that still redeems is the one lie this surface must not tell.
        return false;
      }
      await refresh();
      return true;
    },
    [forecastId, apiPostImpl, refresh],
  );

  return {
    links,
    // Positive test on `state`, per the priority order above — never
    // "not revoked and not expired".
    liveCount: links.filter((l) => l?.state === "live").length,
    loading,
    minting,
    revokingId,
    error: failure ? failure.message : null,
    errorKind: failure ? failure.kind : null,
    errorStatus: failure ? failure.status : null,
    availability,
    availabilityDetail,
    minted,
    mint,
    revoke,
    refresh,
    clearMinted,
    clearError,
  };
}
